import type { ActionRequiredWaitInterval } from './actionRequiredWaitBreakdown';

const DAY_MS = 24 * 60 * 60 * 1000;

export const WAIT_CHART_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#F97316',
  '#6366F1',
  '#84CC16',
];

/** Assign stable colors by holder key (sorted) for pie + bar consistency. */
export function buildWaitChartColorMap(keys: string[]): Map<string, string> {
  const sorted = Array.from(new Set(keys)).sort();
  const map = new Map<string, string>();
  sorted.forEach((key, i) => {
    map.set(key, WAIT_CHART_COLORS[i % WAIT_CHART_COLORS.length]);
  });
  return map;
}

function startOfLocalDay(d: Date | number): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

/** Stable key for Recharts dataKey (holder keys can contain chars Recharts dislikes). */
export function holderSeriesKey(key: string): string {
  return `h_${key.replace(/[^a-zA-Z0-9]/g, '_')}`;
}

export interface ActionRequiredWaitChartSeries {
  key: string;
  holderKey: string;
  displayName: string;
  color: string;
}

export interface BuildActionRequiredWaitChartResult {
  chartData: Record<string, string | number>[];
  series: ActionRequiredWaitChartSeries[];
  empty: boolean;
}

function formatChartDayLabel(ts: number, compact: boolean): string {
  const d = new Date(ts);
  if (compact) {
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Split interval ms across local calendar day boundaries. */
function splitIntervalMsByDay(interval: ActionRequiredWaitInterval): { dayKey: number; holderKey: string; label: string; ms: number }[] {
  const out: { dayKey: number; holderKey: string; label: string; ms: number }[] = [];
  let cursor = interval.startMs;
  const end = interval.endMs;

  while (cursor < end) {
    const dayStart = startOfLocalDay(cursor);
    const dayEnd = dayStart + DAY_MS;
    const sliceEnd = Math.min(end, dayEnd);
    const ms = sliceEnd - cursor;
    if (ms > 0) {
      out.push({
        dayKey: dayStart,
        holderKey: interval.key,
        label: interval.label,
        ms,
      });
    }
    cursor = sliceEnd;
  }

  return out;
}

/**
 * Bucket wait intervals by calendar day (local) and holder.
 * Fills zero days between first and last activity.
 */
export function buildActionRequiredWaitDailyChartData(
  intervals: ActionRequiredWaitInterval[],
): BuildActionRequiredWaitChartResult {
  if (!intervals.length) {
    return { chartData: [], series: [], empty: true };
  }

  const holderMeta = new Map<string, string>();
  intervals.forEach((i) => {
    if (!holderMeta.has(i.key)) holderMeta.set(i.key, i.label);
  });

  const holderKeys = Array.from(holderMeta.keys()).sort();
  const colorMap = buildWaitChartColorMap(holderKeys);

  let minDay = Infinity;
  let maxDay = -Infinity;

  const dayHolderMs = new Map<number, Map<string, number>>();

  for (const interval of intervals) {
    for (const slice of splitIntervalMsByDay(interval)) {
      minDay = Math.min(minDay, slice.dayKey);
      maxDay = Math.max(maxDay, slice.dayKey);
      let bucket = dayHolderMs.get(slice.dayKey);
      if (!bucket) {
        bucket = new Map<string, number>();
        dayHolderMs.set(slice.dayKey, bucket);
      }
      bucket.set(slice.holderKey, (bucket.get(slice.holderKey) || 0) + slice.ms);
    }
  }

  if (minDay === Infinity || maxDay === -Infinity) {
    return { chartData: [], series: [], empty: true };
  }

  const days: number[] = [];
  for (let t = minDay; t <= maxDay; t += DAY_MS) {
    days.push(t);
  }

  const series: ActionRequiredWaitChartSeries[] = holderKeys.map((holderKey) => ({
    key: holderSeriesKey(holderKey),
    holderKey,
    displayName: holderMeta.get(holderKey) || 'Unknown',
    color: colorMap.get(holderKey) || WAIT_CHART_COLORS[0],
  }));

  const useCompactDates = days.length > 12;

  const chartData = days.map((t) => {
    const row: Record<string, string | number> = {
      dateKey: t,
      dateLabel: formatChartDayLabel(t, useCompactDates),
    };
    const bucket = dayHolderMs.get(t);
    holderKeys.forEach((holderKey) => {
      const ms = bucket?.get(holderKey) || 0;
      row[holderSeriesKey(holderKey)] = Math.round((ms / 60000) * 10) / 10;
    });
    return row;
  });

  const hasAnyMinutes = chartData.some((row) =>
    holderKeys.some((hk) => (row[holderSeriesKey(hk)] as number) > 0),
  );

  return {
    chartData,
    series,
    empty: !hasAnyMinutes,
  };
}
